import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { PROFILES } from '../../../utils/constants';
import { format } from 'date-fns';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Container from '../../GeneralComponents/Container';
import { Topbar, Sidebar } from '../components';
import { SERVICE_TYPES, PAYMENT_MODES } from './AddService';

const DetailRow = ({ label, value }) => (
    <>
        <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexDirection={{ xs: 'column', sm: 'row' }}
            padding={2}
        >
            <Typography variant="subtitle2" fontWeight={700}>{label}</Typography>
            <Typography variant="subtitle2" fontWeight={400}>{value ?? '—'}</Typography>
        </Box>
        <Divider />
    </>
);

const getServiceTypeLabel = (value) => SERVICE_TYPES.find((s) => s.value === value)?.label || value || '—';
const getPaymentModeLabel = (value) => PAYMENT_MODES.find((p) => p.value === value)?.label || (value ? value : '—');

const ViewService = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const serviceData = state?.ServiceData;
    const profile = useSelector((s) => s.authReducer.profile);

    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true });
    const [openSidebar, setOpenSidebar] = useState(false);

    const handleSidebarOpen = () => setOpenSidebar(true);
    const handleSidebarClose = () => setOpenSidebar(false);
    const open = isMd ? false : openSidebar;

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        try { return format(new Date(dateStr), 'MMM dd, yyyy'); } catch { return '—'; }
    };

    if (!serviceData) {
        return (
            <Box>
                <AppBar
                    position="fixed"
                    sx={{ backgroundColor: theme.palette.background.paper, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}
                    elevation={0}
                >
                    <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
                        <Topbar onSidebarOpen={handleSidebarOpen} />
                    </Container>
                </AppBar>
                <Sidebar onClose={handleSidebarClose} open={open} variant={isMd ? 'permanent' : 'temporary'} />
                <main>
                    <Box height={{ xs: 94, sm: 102, md: 107 }} />
                    <Box overflow="hidden" marginLeft={{ md: '270px' }} p={3}>
                        <Typography variant="h6">No service data found</Typography>
                        <Button variant="contained" onClick={() => navigate('/services')} sx={{ mt: 2 }}>
                            Go Back to Services
                        </Button>
                    </Box>
                </main>
            </Box>
        );
    }

    return (
        <Box>
            <AppBar
                position="fixed"
                sx={{ backgroundColor: theme.palette.background.paper, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}
                elevation={0}
            >
                <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
                    <Topbar onSidebarOpen={handleSidebarOpen} />
                </Container>
            </AppBar>
            <Sidebar onClose={handleSidebarClose} open={open} variant={isMd ? 'permanent' : 'temporary'} />
            <main>
                <Box height={{ xs: 94, sm: 102, md: 107 }} />
                <Box overflow="hidden" marginLeft={{ md: '270px' }} p={{ xs: 2, md: 4 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/services')}
                        sx={{ mb: 3 }}
                    >
                        Back to Services
                    </Button>

                    <Card variant="outlined" sx={{ maxWidth: 700 }}>
                        <CardHeader
                            title={
                                <Typography variant="h6" fontWeight={700}>
                                    Service Details
                                </Typography>
                            }
                        />
                        <Divider />
                        <CardContent sx={{ p: 0 }}>
                            {profile === PROFILES.ADMIN && (
                                <DetailRow label="Organization" value={serviceData.organizationId?.name || '—'} />
                            )}
                            <DetailRow label="Customer Name" value={serviceData.name || '—'} />
                            <DetailRow label="Mobile Number" value={serviceData.mobileNumber || '—'} />
                            <DetailRow label="Service Type" value={getServiceTypeLabel(serviceData.service)} />
                            <DetailRow label="Amount" value={serviceData.amount != null ? `₹${serviceData.amount}` : '—'} />
                            <DetailRow label="Payment Mode" value={getPaymentModeLabel(serviceData.paymentMode)} />
                            <DetailRow label="Date" value={formatDate(serviceData.date)} />
                            <DetailRow label="Description" value={serviceData.description || '—'} />
                            <DetailRow label="Created" value={formatDate(serviceData.createdAt)} />

                            {/* Documents */}
                            <Box padding={2}>
                                <Typography variant="subtitle2" fontWeight={700} mb={1}>
                                    Documents
                                </Typography>
                                {serviceData.documents?.length > 0 ? (
                                    serviceData.documents.map((doc, index) => (
                                        <Box
                                            key={index}
                                            display="flex"
                                            alignItems="center"
                                            gap={2}
                                            mb={1}
                                            p={1}
                                            sx={{ bgcolor: 'grey.100', borderRadius: 1 }}
                                        >
                                            <InsertDriveFileIcon fontSize="small" color="action" />
                                            <Typography variant="body2" fontWeight={600} flex={1}>
                                                {doc.name}
                                            </Typography>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="primary"
                                                component="a"
                                                href={doc.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                startIcon={<OpenInNewIcon fontSize="small" />}
                                            >
                                                View
                                            </Button>
                                        </Box>
                                    ))
                                ) : (
                                    <Typography variant="body2" color="text.secondary">No documents</Typography>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </main>
        </Box>
    );
};

export default ViewService;
